import * as Y from 'yjs';
import { yMeta, yElements, yLines } from './schema';
import { withHydrationTransaction } from './transact';
import { FORMAT_VERSION } from './doc';

const TABLE_FIELDS_FROM_VALUE = [
    'name', 'description', 'color', 'border',
];
const NOTE_FIELDS_FROM_VALUE = [
    'name', 'description', 'color', 'border',
    'alignmentHorizontal', 'alignmentVertical',
    'isFontMonospace',
];
const PEN_FIELDS_FROM_VALUE = ['color', 'width', 'points', 'newPenId'];

const COLUMN_FIELDS = [
    'id', 'name', 'type', 'description',
    'isPK', 'isFK', 'fkRefTable', 'fkRefColumn',
    'isNotNull', 'isAutoIncrement',
    'isEnum', 'enumValues',
    'defaultValue', 'generatedValue', 'checkValue',
    'indexType',
];

const AREA_FIELDS_FROM_VALUE = [
    'name', 'description', 'color', 'border',
];

function copyDefined(target, source, keys) {
    for (const k of keys) {
        if (source && source[k] !== undefined) target.set(k, source[k]);
    }
}

function buildColumnYMap(rawColumn) {
    const yCol = new Y.Map();
    for (const k of COLUMN_FIELDS) {
        if (rawColumn[k] !== undefined) yCol.set(k, rawColumn[k]);
    }
    // Accept legacy wire format: index: { type } → indexType
    if (rawColumn.index?.type !== undefined) yCol.set('indexType', rawColumn.index.type);
    // 'isRelated' deliberately dropped — derived at render time.
    // 'indexNumber' deliberately dropped — column ordering lives in columnsOrder Y.Array.
    return yCol;
}

function buildElementYMap(raw) {
    const yEl = new Y.Map();
    yEl.set('id',     raw.id);
    yEl.set('type',   raw.type);
    yEl.set('x',      raw.x ?? 0);
    yEl.set('y',      raw.y ?? 0);
    yEl.set('width',  raw.width ?? 0);
    yEl.set('height', raw.height ?? 0);
    yEl.set('areaId', raw.value?.areaId ?? null);

    const value = raw.value ?? {};

    if (raw.type === 'table') {
        copyDefined(yEl, value, TABLE_FIELDS_FROM_VALUE);

        // Legacy stored the table name at value.header.name. Flatten to top-level name.
        if (value.header?.name !== undefined) {
            yEl.set('name', value.header.name);
        }
        // value.header.highlights (red/green diff view) is intentionally dropped —
        // it was an ephemeral computed field, not persistent data.

        // User-shrunk display height for column clipping — mirrors `height`,
        // but only set while the table is actually clipped.
        const displayHeight = raw.displayHeight ?? raw.value?.displayHeight;
        if (displayHeight !== undefined) yEl.set('displayHeight', displayHeight);

        const columnsYMap = new Y.Map();
        const columnsOrder = new Y.Array();
        const rawColumns = value.columns ?? {};
        const colIds = Object.keys(rawColumns);
        for (const colId of colIds) {
            columnsYMap.set(colId, buildColumnYMap(rawColumns[colId]));
        }
        if (colIds.length) columnsOrder.insert(0, colIds);
        yEl.set('columns', columnsYMap);
        yEl.set('columnsOrder', columnsOrder);
    } else if (raw.type === 'note') {
        copyDefined(yEl, value, NOTE_FIELDS_FROM_VALUE);
        // Legacy stored the note body at value.text. Flatten to top-level text.
        if (value.text !== undefined) yEl.set('text', value.text);
    } else if (raw.type === 'pen-segment') {
        copyDefined(yEl, value, PEN_FIELDS_FROM_VALUE);
    }

    return yEl;
}

function buildLineYMap(raw) {
    const yLine = new Y.Map();
    yLine.set('id', raw.id);
    const value = raw.value ?? {};

    // Accept both new names and legacy names (written before format v3).
    const sourceElementId   = value.sourceElementId   ?? value.fromElementId;
    const targetElementId   = value.targetElementId   ?? value.toElementId;
    const sourceColumnId    = value.sourceColumnId    ?? value.fromColumnId  ?? value.fromInsideElementId;
    const targetColumnId    = value.targetColumnId    ?? value.toColumnId    ?? value.toInsideElementId;
    const sourceCardinality = value.sourceCardinality ?? value.fromRelationType;
    const targetCardinality = value.targetCardinality ?? value.toRelationType;
    const strokeStyle       = value.strokeStyle       ?? value.solidType;
    const isReversed        = value.isReversed        ?? value.connectorsReversed;

    if (sourceElementId   !== undefined) yLine.set('sourceElementId',   sourceElementId);
    if (targetElementId   !== undefined) yLine.set('targetElementId',   targetElementId);
    if (sourceColumnId    !== undefined) yLine.set('sourceColumnId',    sourceColumnId);
    if (targetColumnId    !== undefined) yLine.set('targetColumnId',    targetColumnId);
    if (sourceCardinality !== undefined) yLine.set('sourceCardinality', sourceCardinality);
    if (targetCardinality !== undefined) yLine.set('targetCardinality', targetCardinality);
    if (strokeStyle       !== undefined) yLine.set('strokeStyle',       strokeStyle);
    if (isReversed        !== undefined) yLine.set('isReversed',        isReversed);

    copyDefined(yLine, value, ['color', 'description', 'onUpdateTrigger', 'onDeleteTrigger']);

    yLine.set('lineType',  value.lineType  ?? 'reference');
    yLine.set('waypoints', value.waypoints ?? []);

    return yLine;
}

function buildAreaYMap(raw) {
    const yArea = new Y.Map();
    yArea.set('id',     raw.id);
    yArea.set('type',   'area');
    yArea.set('x',      raw.x ?? 0);
    yArea.set('y',      raw.y ?? 0);
    yArea.set('width',  raw.width ?? 0);
    yArea.set('height', raw.height ?? 0);
    const value = raw.value ?? {};
    copyDefined(yArea, value, AREA_FIELDS_FROM_VALUE);
    // 'nestedElementsIds' deliberately dropped — derived from element.areaId.
    return yArea;
}

export function hydrateDoc(doc, fetched) {
    withHydrationTransaction(doc, () => {
        const meta = yMeta(doc);
        meta.set('title',       fetched?.title       ?? '');
        meta.set('description', fetched?.description ?? '');
        meta.set('dbEngine',    fetched?.db_engine   ?? 'postgresql');
        meta.set('formatVersion', FORMAT_VERSION);

        const elements = yElements(doc);
        for (const [id, raw] of Object.entries(fetched?.data?.elements ?? {})) {
            elements.set(id, buildElementYMap({ ...raw, id }));
        }

        const lines = yLines(doc);
        for (const [id, raw] of Object.entries(fetched?.data?.lines ?? {})) {
            lines.set(id, buildLineYMap({ ...raw, id }));
        }

        for (const [id, raw] of Object.entries(fetched?.data?.areas ?? {})) {
            elements.set(id, buildAreaYMap({ ...raw, id }));
        }
    });
}
